import {
  ArrowTopRightOnSquareIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  ExclamationCircleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';

import {
  getUserNames,
  groupActionItems,
  parseActionItems,
} from '../utils/transcript';

import type { FirefliesTranscript } from '../types';

const TRUNCATE_LENGTH = 120;
const MAX_VISIBLE_TOPICS = 3;

const getTruncatedSummary = (transcript: FirefliesTranscript): string => {
  const text =
    transcript.summary?.short_summary ||
    transcript.summary?.overview ||
    '';
  if (!text) return '';
  const cleaned = text.replace(/\*\*/g, '');
  return cleaned.length > TRUNCATE_LENGTH ? `${cleaned.slice(0, TRUNCATE_LENGTH)}...` : cleaned;
};

const getTopics = (transcript: FirefliesTranscript): string[] => {
  return transcript.summary?.topics_discussed ?? transcript.summary?.keywords ?? [];
};

interface TranscriptCardProps {
  transcript: FirefliesTranscript;
  userEmail?: string;
  onOpenDetail: (transcript: FirefliesTranscript) => void;
  onSaveToNotes?: (transcript: FirefliesTranscript) => void;
  onAddToChecklist?: (transcript: FirefliesTranscript) => void;
  hasNotesWidget?: boolean;
  hasChecklistWidget?: boolean;
}

export default function TranscriptCard({
  transcript,
  userEmail,
  onOpenDetail,
  onSaveToNotes,
  onAddToChecklist,
  hasNotesWidget,
  hasChecklistWidget,
}: TranscriptCardProps) {
  const { t } = useTranslation();
  const summary = getTruncatedSummary(transcript);
  const parsed = parseActionItems(transcript.summary?.action_items);
  const names = getUserNames(userEmail ?? '', transcript.meeting_attendees);
  const groups = groupActionItems(parsed, names);
  const userActionCount = groups
    .filter((g) => g.isCurrentUser)
    .reduce((sum, g) => sum + g.items.length, 0);
  const topics = getTopics(transcript);
  const visibleTopics = topics.slice(0, MAX_VISIBLE_TOPICS);
  const extraTopicCount = topics.length - MAX_VISIBLE_TOPICS;

  const date = transcript.date ? new Date(transcript.date) : new Date();
  const duration = transcript.duration
    ? `${Math.round(transcript.duration)} min`
    : null;
  const participantCount = transcript.participants?.length ?? 0;

  return (
    <div
      className="group p-4 bg-white rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all cursor-pointer"
      onClick={() => onOpenDetail(transcript)}
    >
      <div className="flex justify-between items-start gap-3">
        <div className="grow min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate leading-snug">
            {transcript.title}
          </h3>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-xs text-gray-400">
            <span className="inline-flex items-center gap-1">
              <ClockIcon className="w-3.5 h-3.5" />
              {formatDistanceToNow(date, { addSuffix: true })}
            </span>
            {duration && (
              <span className="inline-flex items-center gap-1">
                <ChatBubbleLeftRightIcon className="w-3.5 h-3.5" />
                {duration}
              </span>
            )}
            {participantCount > 0 && (
              <span className="inline-flex items-center gap-1">
                <UserGroupIcon className="w-3.5 h-3.5" />
                {participantCount}
              </span>
            )}
          </div>

          {summary && (
            <p className="text-xs text-gray-500 mt-2 leading-relaxed line-clamp-2">
              {summary}
            </p>
          )}

          {visibleTopics.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
              {visibleTopics.map((topic) => (
                <span
                  key={topic}
                  className="px-2 py-0.5 text-[11px] font-medium bg-gray-100 text-gray-600 rounded-full"
                >
                  {topic}
                </span>
              ))}
              {extraTopicCount > 0 && (
                <span className="text-[11px] text-gray-400">
                  +{extraTopicCount} more
                </span>
              )}
            </div>
          )}

          {userActionCount > 0 && (
            <div className="flex items-center gap-2 mt-2.5">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 rounded-full">
                <ExclamationCircleIcon className="w-3.5 h-3.5" />
                {userActionCount} action item{userActionCount > 1 ? 's' : ''} for you
              </span>
            </div>
          )}
        </div>

        <div
          className="flex flex-col items-end gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <a
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
            href={transcript.transcript_url}
            rel="noopener noreferrer"
            target="_blank"
            title={t('fireflies.viewTranscript')}
          >
            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
          </a>
          {hasNotesWidget && onSaveToNotes && (
            <button
              className="px-2 py-1 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600 text-[11px] whitespace-nowrap"
              title={t('fireflies.saveToNotes')}
              onClick={() => onSaveToNotes(transcript)}
            >
              {t('fireflies.saveToNotes')}
            </button>
          )}
          {hasChecklistWidget && userActionCount > 0 && onAddToChecklist && (
            <button
              className="px-2 py-1 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600 text-[11px] whitespace-nowrap"
              title={t('fireflies.addToChecklist')}
              onClick={() => onAddToChecklist(transcript)}
            >
              {t('fireflies.addToChecklist')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
